// Pure helpers for the single-metric panel's "compare against prior period" mode.
// The prior period is computed entirely client-side by shifting the resolved
// timeframe's start/end dates; results are fed to a second KPI summary call.

// "All Time" has no meaningful prior period, but it's hard to identify: its resolved
// dates look like any wide custom range, and timeframe ids are NOT reliable — admin-
// created timeframes get per-client UUIDs (e.g. the YTD preset observed as
// "1747020f-…"), and some clients don't have an All Time preset at all. We best-effort
// match the cube19-default id below AND check the display label (catches UUID-based
// installs with the default "All Time" label); when neither matches, the checkbox stays
// enabled and load degrades gracefully — enabling it on a true All-Time timeframe yields
// an empty prior window, so no % change is shown (no crash, no wrong number).
const ALL_TIME_ID = "all_time";

const normalise = value => (value == null ? "" : String(value)).replace(/[^a-z0-9]/gi, "").toLowerCase();

export const isAllTime = timeframe =>
  !!timeframe && (
    normalise(timeframe.get("id")) === normalise(ALL_TIME_ID) ||
    normalise(timeframe.get("label") ?? "") === normalise(ALL_TIME_ID) ||
    normalise(timeframe.get("name") ?? "") === normalise(ALL_TIME_ID)
  );

// Year-to-date is detected structurally, not by id: it starts on Jan 1, ends in the
// same year before Dec 31, AND ends on a day that is not the last day of its month
// (so Q1 Jan 1→Mar 31 is not mistaken for YTD). "This year" (Jan 1 → Dec 31) is
// deliberately not treated as YTD — a plain day-shift already maps it to last year.
const isYearToDate = timeframe => {
  const start = timeframe.get("start");
  const end = timeframe.get("end");
  if (!start || !end || !start.isValid() || !end.isValid()) return false;
  const startsOnJan1 = start.month() === 0 && start.date() === 1;
  const endsBeforeYearEnd = end.year() === start.year() && (end.month() < 11 || end.date() < 31);
  const notMonthEnd = end.date() !== end.clone().endOf("month").date();
  // Require at least 28 days to avoid misidentifying short custom ranges that
  // happen to start on Jan 1 (e.g. "1 Jan – 12 Jan") as year-to-date.
  const longEnough = end.diff(start, "days") >= 28;
  return startsOnJan1 && endsBeforeYearEnd && notMonthEnd && longEnough;
};

// Returns {start, end} moments for the prior period, or null when not computable.
// - All time: not computable.
// - Year to date (special case): Jan 1 last year → the same day last year.
// - Full calendar year (Jan 1 → Dec 31): the same calendar year one year prior.
// - Full calendar quarter (Q start → Q end): the same quarter one quarter prior.
// - Full calendar month (1st → last day): the same month one month prior.
// - Everything else (last N days, rolling 12m, partial month/quarter, custom):
//   the adjacent, equal-length window immediately before `start` (day-shift).
export const computePriorTimeframe = timeframe => {
  if (!timeframe || isAllTime(timeframe)) return null;

  const start = timeframe.get("start");
  const end = timeframe.get("end");
  if (!start || !end || !start.isValid() || !end.isValid()) return null;

  if (isYearToDate(timeframe)) {
    return {
      start: start.clone().subtract(1, "year"),
      end: end.clone().subtract(1, "year")
    };
  }

  // Full calendar year: Jan 1 → Dec 31 of the same year
  if (start.month() === 0 && start.date() === 1 &&
      end.month() === 11 && end.date() === 31 &&
      start.year() === end.year()) {
    return {
      start: start.clone().subtract(1, "year"),
      end: start.clone().subtract(1, "day")
    };
  }

  // Full calendar quarter: first day of a quarter → last day of the same quarter
  const isFirstDayOfQuarter = start.date() === 1 && start.month() % 3 === 0;
  const isLastDayOfQuarter = end.date() === end.clone().endOf("month").date() && end.month() % 3 === 2;
  const sameQuarter = start.year() === end.year() &&
    Math.floor(start.month() / 3) === Math.floor(end.month() / 3);
  if (isFirstDayOfQuarter && isLastDayOfQuarter && sameQuarter) {
    return {
      start: start.clone().subtract(3, "months"),
      end: start.clone().subtract(1, "day")
    };
  }

  // Full calendar month: 1st → last day of the same month
  const isFirstDayOfMonth = start.date() === 1;
  const isLastDayOfMonth = end.date() === end.clone().endOf("month").date();
  const sameMonth = start.year() === end.year() && start.month() === end.month();
  if (isFirstDayOfMonth && isLastDayOfMonth && sameMonth) {
    return {
      start: start.clone().subtract(1, "month"),
      end: start.clone().subtract(1, "day")
    };
  }

  // Everything else: equal-length adjacent window
  const durationDays = end.diff(start, "days");
  return {
    start: start.clone().subtract(durationDays + 1, "days"),
    end: start.clone().subtract(1, "day")
  };
};

export const isPriorPeriodComputable = timeframe => computePriorTimeframe(timeframe) !== null;
